// useLayoutEditorLayoutMutations.js

import { useCallback } from 'react';

// Utilities
import {
	buildLayoutRow,
	buildLayoutSlot,
	buildLayoutStack,
	canContainerAcceptChildKind,
	cloneLayout,
	collectBlockKeysForNode,
	findParentNodeKey,
	isContainerKind,
	moveListItem,
	pruneEmptyAncestorContainers,
	removeBlockKeys,
	removeNodeDeep,
} from './pageEditorLayoutUtils';

/**
 * useLayoutEditorLayoutMutations
 *
 * Owns the structural layout mutation handlers for rows, slots, and container children.
 *
 * @param {object} params - Hook parameters.
 * @param {number} params.assignedBlockCount - Count of currently assigned blocks.
 * @param {object} params.blocksByKey - Current block lookup map.
 * @param {object} params.layout - Current layout tree.
 * @param {function} params.persistLayout - Persists layout-only changes.
 * @param {function} params.persistPage - Persists layout and block changes together.
 * @param {function|null} [params.setToolbarRowKey=null] - Optional toolbar row setter used after split operations.
 * @param {function|null} [params.setToolbarSlotKey=null] - Optional toolbar slot setter used after split operations.
 *
 * @returns {object} Structural layout mutation handlers.
 */
const useLayoutEditorLayoutMutations = ({
	assignedBlockCount,
	blocksByKey,
	layout,
	persistLayout,
	persistPage,
	setToolbarRowKey = null,
	setToolbarSlotKey = null,
}) => {
	/**
	 * handleInsertRowAtRoot
	 *
	 * Inserts a new row at the requested root index.
	 *
	 * @param {number} insertIndex - Target root insertion index.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleInsertRowAtRoot = useCallback(async (insertIndex) => {
		const nextLayout = cloneLayout(layout);
		const { rowNode, slotNodes } = buildLayoutRow(1);
		const rootChildren = Array.isArray(nextLayout.nodes[nextLayout.rootNodeKey]?.children)
			? [...nextLayout.nodes[nextLayout.rootNodeKey].children]
			: [];
		const resolvedIndex = Number.isInteger(insertIndex)
			? Math.min(Math.max(insertIndex, 0), rootChildren.length)
			: rootChildren.length;

		nextLayout.nodes[rowNode.key] = rowNode;
		slotNodes.forEach((slotNode) => {
			nextLayout.nodes[slotNode.key] = slotNode;
		});

		rootChildren.splice(resolvedIndex, 0, rowNode.key);
		nextLayout.nodes[nextLayout.rootNodeKey].children = rootChildren;
		await persistLayout(nextLayout);
	}, [layout, persistLayout]);

	/**
	 * handleInsertSlotInContainer
	 *
	 * Inserts a new empty slot into the requested container index.
	 *
	 * @param {string} containerKey - Target container key.
	 * @param {number} insertIndex - Target container insertion index.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleInsertSlotInContainer = useCallback(async (containerKey, insertIndex) => {
		const cleanedContainerKey = String(containerKey || '').trim();
		if (!cleanedContainerKey) return;

		const nextLayout = cloneLayout(layout);
		const containerNode = nextLayout.nodes[cleanedContainerKey];
		if (!containerNode || !isContainerKind(containerNode.kind)) return;
		const containerChildren = Array.isArray(containerNode.children) ? [...containerNode.children] : [];
		const resolvedIndex = Number.isInteger(insertIndex)
			? Math.min(Math.max(insertIndex, 0), containerChildren.length)
			: containerChildren.length;

		const slotNode = buildLayoutSlot();
		nextLayout.nodes[slotNode.key] = slotNode;
		containerChildren.splice(resolvedIndex, 0, slotNode.key);
		containerNode.children = containerChildren;
		await persistLayout(nextLayout);
	}, [layout, persistLayout]);

	/**
	 * handleAddSlotToContainer
	 *
	 * Appends a new empty slot to the end of a container.
	 *
	 * @param {string} containerKey - Target container key.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleAddSlotToContainer = useCallback(async (containerKey) => {
		const cleanedContainerKey = String(containerKey || '').trim();
		if (!cleanedContainerKey) return;

		const containerNode = layout.nodes[cleanedContainerKey];
		const childCount = Array.isArray(containerNode?.children) ? containerNode.children.length : 0;
		await handleInsertSlotInContainer(cleanedContainerKey, childCount);
	}, [handleInsertSlotInContainer, layout.nodes]);

	/**
	 * handleMoveChildInContainer
	 *
	 * Moves a child node within its current container.
	 *
	 * @param {string} parentKey - Parent container key.
	 * @param {string} childKey - Child node key to move.
	 * @param {number} offset - Relative movement offset.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleMoveChildInContainer = useCallback(async (parentKey, childKey, offset) => {
		const cleanedParentKey = String(parentKey || '').trim();
		const cleanedChildKey = String(childKey || '').trim();
		if (!cleanedParentKey || !cleanedChildKey || !Number.isInteger(offset) || offset === 0) return;

		const nextLayout = cloneLayout(layout);
		const parentNode = nextLayout.nodes[cleanedParentKey];
		if (!parentNode || !Array.isArray(parentNode.children)) return;

		const children = [...parentNode.children];
		const fromIndex = children.indexOf(cleanedChildKey);
		if (fromIndex < 0) return;

		const toIndex = fromIndex + offset;
		if (toIndex < 0 || toIndex >= children.length) return;

		parentNode.children = moveListItem(children, fromIndex, toIndex);
		await persistLayout(nextLayout);
	}, [layout, persistLayout]);

	/**
	 * handleDeleteChildFromContainer
	 *
	 * Deletes a child node from its container while protecting the last top-level row and last assigned block.
	 *
	 * @param {string} parentKey - Parent container key.
	 * @param {string} childKey - Child node key to delete.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleDeleteChildFromContainer = useCallback(async (parentKey, childKey) => {
		const cleanedParentKey = String(parentKey || '').trim();
		const cleanedChildKey = String(childKey || '').trim();
		if (!cleanedParentKey || !cleanedChildKey) return;

		const parentNode = layout.nodes[cleanedParentKey] || null;
		const childNode = layout.nodes[cleanedChildKey] || null;
		const parentChildren = Array.isArray(parentNode?.children) ? parentNode.children : [];
		const isLastTopLevelRow = cleanedParentKey === layout.rootNodeKey
			&& childNode?.kind === 'row'
			&& parentChildren.length <= 1;

		if (isLastTopLevelRow) return;

		const blockKeysToDelete = collectBlockKeysForNode(layout, cleanedChildKey)
			.filter((blockKey) => Boolean(blocksByKey[blockKey]));
		if (blockKeysToDelete.length > 0 && assignedBlockCount <= blockKeysToDelete.length) {
			return;
		}

		const nextLayout = removeNodeDeep(layout, cleanedChildKey);
		const cleanedLayout = pruneEmptyAncestorContainers(nextLayout, cleanedParentKey);
		const nextBlocksByKey = removeBlockKeys(blocksByKey, blockKeysToDelete);
		await persistPage(cleanedLayout, nextBlocksByKey);
	}, [assignedBlockCount, blocksByKey, layout, persistPage]);

	/**
	 * handleSplitSlot
	 *
	 * Splits a slot by inserting a sibling slot directly or by wrapping it in a new container.
	 *
	 * @param {string} slotKey - Slot key to split.
	 * @param {string} nextContainerKind - Target container kind.
	 * @returns {Promise<void>} Resolves when the layout save completes.
	 */
	const handleSplitSlot = useCallback(async (slotKey, nextContainerKind) => {
		const cleanedSlotKey = String(slotKey || '').trim();
		const cleanedContainerKind = String(nextContainerKind || '').trim();
		if (!cleanedSlotKey || !isContainerKind(cleanedContainerKind)) return;

		const nextLayout = cloneLayout(layout);
		const slotNode = nextLayout.nodes[cleanedSlotKey];
		if (!slotNode || slotNode.kind !== 'block_slot') return;

		const parentKey = findParentNodeKey(nextLayout, cleanedSlotKey);
		const parentNode = nextLayout.nodes[parentKey];
		if (!parentNode || !isContainerKind(parentNode.kind)) return;

		const siblingSlot = buildLayoutSlot();
		nextLayout.nodes[siblingSlot.key] = siblingSlot;

		let nextToolbarContainerKey = parentKey;

		if (parentNode.kind === cleanedContainerKind) {
			const currentIndex = parentNode.children.indexOf(cleanedSlotKey);
			if (currentIndex < 0) return;
			parentNode.children.splice(currentIndex + 1, 0, siblingSlot.key);
		} else {
			if (!canContainerAcceptChildKind(parentNode.kind, cleanedContainerKind)) return;

			const replacementContainer = cleanedContainerKind === 'stack'
				? buildLayoutStack(0).stackNode
				: buildLayoutRow(0).rowNode;

			replacementContainer.children = [cleanedSlotKey, siblingSlot.key];
			nextLayout.nodes[replacementContainer.key] = replacementContainer;
			parentNode.children = parentNode.children.map((childKey) => (
				childKey === cleanedSlotKey ? replacementContainer.key : childKey
			));
			nextToolbarContainerKey = replacementContainer.key;
		}

		setToolbarRowKey?.(nextToolbarContainerKey);
		setToolbarSlotKey?.(cleanedSlotKey);
		await persistLayout(nextLayout);
	}, [layout, persistLayout, setToolbarRowKey, setToolbarSlotKey]);

	return {
		handleAddSlotToContainer,
		handleDeleteChildFromContainer,
		handleInsertRowAtRoot,
		handleInsertSlotInContainer,
		handleMoveChildInContainer,
		handleSplitSlot,
	};
};

export default useLayoutEditorLayoutMutations;